<template>
  <section class="spot-hist-card">
    <div class="spot-hist-card__header">
      <div class="title-container">
        <span class="symbol-icon"></span>
        <span class="title-text">{{ $str.getKey('收益管理/各省现货历史电价') }}</span>
      </div>

      <div class="btns-container">
        <div class="filters-scroll">
          <el-form class="filter-form" label-position="left" :model="formData" @submit.prevent>
            <el-form-item class="query-item query-item--area" :label="$str.getKey('收益管理/地区')">
              <el-cascader
                class="field-wide"
                v-model="formData.areaCode"
                :options="adOptions"
                :props="cascaderProps"
                :show-all-levels="false"
                clearable
              />
            </el-form-item>
            <el-form-item class="query-item query-item--date" :label="$str.getKey('收益管理/日期')">
              <el-date-picker
                class="field-range"
                v-model="formData.priceDate"
                type="date"
                placeholder="请选择日期"
                value-format="YYYY-MM-DD"
                clearable
              />
            </el-form-item>
            <el-form-item class="query-item query-item--type" label="类型">
              <el-select
                class="field-type"
                v-model="formData.priceType"
                placeholder="请选择"
                clearable
              >
                <el-option label="实时电价" value="1" />
                <el-option label="日前电价" value="2" />
              </el-select>
            </el-form-item>
          </el-form>
        </div>

        <div class="actions">
          <el-button class="meter-feature-btn meter-feature-btn--lg" type="primary" :loading="loading" @click="onQuery">查询</el-button>
        </div>
      </div>
    </div>

    <div class="spot-hist-card__body">
      <div class="spot-table-wrap" v-loading="loading">
        <div class="spot-table-inner">
          <v-table
            density="compact"
            class="bg-transparent flex-grow-1 gm-table ops-table ops-spot-table"
            style="width: 100%"
          >
            <thead>
              <tr>
                <th>地区</th>
                <th>日期</th>
                <th>时刻</th>
                <th>价格类型</th>
                <th class="text-end">市场价(元)</th>
                <th class="text-end">服务费(元)</th>
                <th class="text-end">成交价(元)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in pageRows" :key="row.areaCode + '-' + row.priceDate + '-' + idx">
                <td class="cell-text">{{ row.areaName }}</td>
                <td class="cell-text">{{ row.priceDate }}</td>
                <td class="cell-text">{{ row.timeSlot }}</td>
                <td class="cell-text">{{ row.priceTypeLabel }}</td>
                <td class="text-end"><span class="cell-text">{{ row.marketPrice }}</span></td>
                <td class="text-end"><span class="cell-text">{{ row.servicePrice }}</span></td>
                <td class="text-end"><span class="cell-text">{{ row.finalPrice }}</span></td>
              </tr>
              <tr v-if="pageRows.length === 0 && !loading">
                <td class="empty-cell" colspan="7">暂无数据（请调整筛选条件）</td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </div>

      <TablePagination
        v-model="pageCtrl.currentPage"
        v-model:page-size="pageCtrl.pageSize"
        :total="pageCtrl.total"
        :disabled="loading"
        @change="handlePageChange"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import dayjs from 'dayjs'
import { getHistoryPriceData } from '@/api/business-operations'
import TablePagination from '@/components/TablePagination/index.vue'

const $str = window.$str

defineOptions({ name: 'SpotHistoryListPanel' })

const props = defineProps({
  adOptions: { type: Array, default: () => [] },
})

const adOptions = computed(() => props.adOptions)

const cascaderProps = reactive({
  checkStrictly: true,
  expandTrigger: 'hover',
  emitPath: false,
})

const loading = ref(false)

const formData = reactive({
  areaCode: '',
  priceDate: '',
  priceType: '',
})

/** 当前页表格行（服务端分页，一次列表接口返回一页） */
const pageRows = ref([])

const pageCtrl = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0,
})

const handlePageChange = () => {
  fetchList()
}

/** 解析列表接口返回（兼容 rows/records/list、total/totalCount） */
function normalizeListPayload(res) {
  const root = res?.data != null ? res.data : res
  if (root == null) return { list: [], total: 0 }
  if (Array.isArray(root)) return { list: root, total: root.length }
  const list = root.records ?? root.rows ?? root.list ?? root.data
  const arr = Array.isArray(list) ? list : []
  const total = root.total ?? root.totalCount ?? root.totalElements ?? arr.length
  return { list: arr, total: Number(total) || 0 }
}

function mapHistoryRow(it, fallbackAreaCode, fallbackAreaName) {
  const raw =
    it?.priceDate ??
    it?.snapshotDate ??
    it?.date ??
    it?.day ??
    it?.price_date
  const priceDate = raw ? dayjs(raw).format('YYYY-MM-DD') : ''
  const formatNumber = (v) => {
    const n = Number(v)
    if (!Number.isFinite(n)) return '-'
    return n.toFixed(6)
  }
  const priceType = it?.priceType ?? it?.price_type
  const priceTypeLabel = priceType === 1 || priceType === '1'
    ? '实时电价'
    : priceType === 2 || priceType === '2'
      ? '日前电价'
      : '-'
  const sourceType = it?.sourceType ?? it?.source_type
  const sourceTypeLabel = sourceType === 2 ? '人工调整' : sourceType === 1 ? '市场接口' : '-'
  return {
    areaCode: String(it?.areaCode ?? it?.area_code ?? fallbackAreaCode ?? ''),
    areaName: String(it?.areaName ?? it?.area_name ?? fallbackAreaName ?? ''),
    priceDate,
    timeSlot: String(it?.timeSlot ?? it?.time_slot ?? ''),
    priceType,
    priceTypeLabel,
    sourceType,
    sourceTypeLabel,
    marketPrice: formatNumber(it?.marketPrice ?? it?.market_price),
    servicePrice: formatNumber(it?.servicePrice ?? it?.service_price),
    finalPrice: formatNumber(it?.finalPrice ?? it?.final_price),
  }
}

const resolveAreaPathLabel = (value) => {
  const target = String(value ?? '')
  if (!target) return ''
  const dfs = (list, path) => {
    if (!Array.isArray(list)) return null
    for (const it of list) {
      const nextPath = [...path, String(it?.label ?? '')].filter(Boolean)
      if (String(it?.value) === target) return nextPath
      const hit = dfs(it?.children, nextPath)
      if (hit) return hit
    }
    return null
  }
  const labels = dfs(adOptions.value, []) || []
  return labels.join('')
}

async function fetchList() {
  if (!formData.areaCode) {
    window.dispatchNotify?.({ type: 'warning', message: '请选择地区' })
    return
  }
  if (!formData.priceDate) {
    window.dispatchNotify?.({ type: 'warning', message: '请选择日期' })
    return
  }
  const selectedDate = dayjs(formData.priceDate).format('YYYY-MM-DD')

  loading.value = true
  try {
    const fallbackName = resolveAreaPathLabel(formData.areaCode) || ''
    const res = await getHistoryPriceData({
      areaCode: formData.areaCode || undefined,
      priceDate: selectedDate,
      priceType: formData.priceType || undefined,
      pageNum: pageCtrl.currentPage,
      pageSize: pageCtrl.pageSize,
    })
    const { list, total } = normalizeListPayload(res)
    pageRows.value = list.map((it) => mapHistoryRow(it, formData.areaCode, fallbackName))
    pageCtrl.total = total
  } catch (e) {
    pageRows.value = []
    pageCtrl.total = 0
  } finally {
    loading.value = false
  }
}

const onQuery = async () => {
  pageCtrl.currentPage = 1
  await fetchList()
}
</script>

<style scoped lang="scss">
@import '../../_shared/feature-button.scss';

.spot-hist-card {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
}

.spot-hist-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: linear-gradient(180deg, rgba(58, 104, 255, 0.05) 0%, rgba(255, 255, 255, 0) 72%);
  min-height: 56px;
  box-sizing: border-box;
  overflow-x: hidden;
}

.title-container {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 170px;
  flex: 0 0 auto;
}

.symbol-icon {
  width: 4px;
  height: 20px;
  background: #485e91;
  border-radius: 2px;
}

.title-text {
  font-size: 16px;
  font-weight: 600;
  color: rgba(46, 46, 46, 0.88);
  white-space: nowrap;
}

.btns-container {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  max-width: 100%;
  overflow-x: hidden;
  gap: 12px;
}

.filters-scroll {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.filters-scroll::-webkit-scrollbar {
  height: 4px;
}
.filters-scroll::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.12);
  border-radius: 999px;
}
.filters-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.filter-form {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  align-items: center;
  width: auto;
  min-width: 0;
  max-width: 100%;
  margin-left: auto;
}

.filter-form :deep(.el-form-item) {
  margin: 0;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  min-height: 40px;
}

.query-item--area {
  width: 312px;
}

.query-item--date {
  width: 332px;
}

.filter-form :deep(.el-form-item__label) {
  width: 60px;
  justify-content: flex-end;
  line-height: 40px;
  font-size: 16px;
  color: rgba(46, 46, 46, 0.65);
  padding: 0;
  padding-right: 8px;
  white-space: nowrap;
}

.filter-form :deep(.el-form-item__content) {
  line-height: 40px;
  display: flex;
  align-items: center;
  min-height: 40px;
  margin: 0 !important;
}

.filter-form :deep(.el-input__wrapper),
.filter-form :deep(.el-select__wrapper),
.filter-form :deep(.el-date-editor .el-input__wrapper),
.filter-form :deep(.el-cascader .el-input__wrapper) {
  /* 对齐电价维护头部筛选（MeterData 风格） */
  background: #fff;
  border: 1px solid rgba(192, 207, 218, 0.3);
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 0 16px;
  min-height: 40px;
  height: 40px;
  align-items: center;
}

.filter-form :deep(.field-wide) {
  width: 240px;
}

.filter-form :deep(.field-range) {
  width: 260px;
}

.filter-form :deep(.field-type) {
  width: 140px;
}

.filter-form :deep(.field-range.el-date-editor) {
  width: 252px;
}

.filter-form :deep(.field-range .el-range-input) {
  width: 78px;
  min-width: 78px;
  flex: 0 0 78px;
}

.filter-form :deep(.field-range .el-range-separator) {
  width: 24px;
  text-align: center;
}

.query-item--area :deep(.el-cascader) {
  width: 240px !important;
}

.query-item--date :deep(.el-date-editor) {
  width: 252px !important;
  height: 40px !important;
  display: inline-flex;
  align-items: center;
}

.query-item--date :deep(.el-form-item__content) {
  display: flex;
  align-items: center;
  min-height: 40px;
}

.query-item--date :deep(.el-input__wrapper) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.query-item--date :deep(.el-date-editor .el-input__wrapper) {
  height: 40px !important;
  min-height: 40px !important;
  line-height: 40px !important;
}

.actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.spot-hist-card__body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

.spot-table-wrap {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.78);
  min-height: 520px;
  display: flex;
  overflow-y: visible;
}

.spot-table-inner {
  width: 100%;
  min-width: 0;
}

.ops-spot-table {
  width: 100%;
  max-width: 100%;
  min-height: 0;
  background: transparent !important;
}

.ops-spot-table :deep(.v-table__wrapper) {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  background: transparent !important;
}

.ops-spot-table :deep(table) {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: transparent;
}

.ops-spot-table :deep(thead th),
.ops-spot-table :deep(tbody td) {
  min-width: 0 !important;
  max-width: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-text {
  color: rgba(46, 46, 46, 0.86);
}

.empty-cell {
  text-align: center;
  color: rgba(46, 46, 46, 0.5);
  padding: 18px 0;
}

 /* 按钮统一白字（本页 UI 规范） */
   :deep(.el-button:not(.is-link)) {
    --el-button-text-color: #fff;
    color: #fff;
  }
</style>

